import { useEffect, useState } from "react";
import {
  DimensionEvent,
  DimensionEventStatus,
} from "@dimensional-engineering/dimensional-models";
import { useSelector } from "react-redux";

import { RootState } from "@/redux/store";

import { shortFormToLongFormDimensionSlugMap } from "@/assets/traitInfo/dimensionData";
import { callFirebaseFunctions } from "@/_firebase/callFirebaseFunctions";

type Props = {
  numFriendsRequired?: number | null | undefined;
  requiredDimensionSlugs?: string[] | null | undefined;
  numEndorsementsRequired?: number | null | undefined;
  numPeerAssessorsRequired?: number | null | undefined;
  peerPackageCompletion?: {
    packageSlug: string;
    packageName: string;
    numRequired: number;
  };
  numPeerRatingsRequired?: number | null | undefined;
  numSuccessfulInvitesRequired?: number | null | undefined;
  userSuccessfulInvites: string[] | [] | null | undefined;
  userDimensionEvents: DimensionEvent[] | null;
  userConnectionCount: number;
};

export default function useDiscoveryGateHook(props: Props) {
  const [remainingDimensionSlugs, setRemainingDimensionSlugs] = useState<
    string[]
  >([]);
  const [errorState, setErrorState] = useState<boolean>(false);
  const [neededDimensionsCompleted, setNeededDimensionsCompleted] =
    useState<boolean>(true);
  const [connectionComplete, setConnectionComplete] = useState<boolean>(true);
  const [invitesComplete, setInvitesComplete] = useState<boolean>(true);
  const [peerRatingsComplete, setPeerRatingsComplete] = useState<boolean>(true);
  const [peerRatingCount, setPeerRatingCount] = useState<number>(0);
  const [peerAssessorComplete, setPeerAssessorComplete] =
    useState<boolean>(true);

  const userState = useSelector((state: RootState) => state.user);

  async function checkNumberOfPeerRatings() {
    let count = 0;

    const call = callFirebaseFunctions("fetchNumPeerRatings");
    await call().then((res) => {
      setPeerRatingCount(res.data as number);
      count = res.data as number;
    });

    return count;
  }

  useEffect(() => {
    if (props.requiredDimensionSlugs) {
      //dimension gated
      const finishedSlugs: string[] = [];
      const notFinishedSlugs: string[] = [];

      props.requiredDimensionSlugs.forEach((slug) => {
        const foundDimensionEvent = props.userDimensionEvents?.find(
          (e) => e.dimensionSlug === slug
        );
        if (!foundDimensionEvent) {
          notFinishedSlugs.push(slug);
        } else {
          //has dimension event
          if (
            foundDimensionEvent.event === DimensionEventStatus.deferred ||
            foundDimensionEvent.event === DimensionEventStatus.deferredGated
          ) {
            //has long form so we have to look for it
            const longFormFoundDimensionEvent = props.userDimensionEvents?.find(
              (e) =>
                e.dimensionSlug === shortFormToLongFormDimensionSlugMap[slug]
            );

            if (!longFormFoundDimensionEvent) {
              // long form dim event not created for some reason
              finishedSlugs.push(slug);
            } else if (
              longFormFoundDimensionEvent.event ===
                DimensionEventStatus.complete ||
              longFormFoundDimensionEvent.event ===
                DimensionEventStatus.reset ||
              longFormFoundDimensionEvent.event ===
                DimensionEventStatus.resetGated
            ) {
              //long form has been completed at least once
              if (
                longFormFoundDimensionEvent.event !==
                DimensionEventStatus.resetGated
              ) {
                //long form not gated
                finishedSlugs.push(slug);
              } else {
                //long form gated
                notFinishedSlugs.push(slug);
              }
            } else {
              //long form is not completed once yet. In intermittent state
              //between finish of short form and start of long form
              if (foundDimensionEvent.event === DimensionEventStatus.deferred) {
                finishedSlugs.push(slug);
              } else {
                notFinishedSlugs.push(slug);
              }
            }
          } else {
            //doesn't have long form
            if (
              foundDimensionEvent.event === DimensionEventStatus.complete ||
              foundDimensionEvent.event === DimensionEventStatus.reset
            ) {
              //not gated
              finishedSlugs.push(slug);
            } else {
              //gated
              notFinishedSlugs.push(slug);
            }
          }
        }
      });
      setRemainingDimensionSlugs(notFinishedSlugs);
      if (notFinishedSlugs.length === 0) {
        setNeededDimensionsCompleted(true);
      } else {
        setNeededDimensionsCompleted(false);
      }
    }

    if (
      props.numSuccessfulInvitesRequired &&
      props.userSuccessfulInvites?.length
    ) {
      if (
        props.userSuccessfulInvites.length < props.numSuccessfulInvitesRequired
      ) {
        setInvitesComplete(false);
      }
    } else if (
      props.numSuccessfulInvitesRequired &&
      !props.userSuccessfulInvites
    ) {
      setInvitesComplete(false);
    }
    ///number of successful invites

    if (props.numFriendsRequired) {
      if (props.userConnectionCount < props.numFriendsRequired) {
        setConnectionComplete(false);
      }
    }
    //number of connections

    if (props.numPeerRatingsRequired) {
      checkNumberOfPeerRatings().then((res) => {
        console.log(res);
        if (
          props?.numPeerRatingsRequired &&
          props?.numPeerRatingsRequired > res
        ) {
          setPeerRatingsComplete(false);
        }
      });
    }

    //number of peer ratings

    if (props.numPeerAssessorsRequired) {
      const numberOfPeerAssesors =
        userState.memberProfile?.peerAssesseeUids?.length;
      if (numberOfPeerAssesors) {
        if (numberOfPeerAssesors < props.numPeerAssessorsRequired) {
          setPeerAssessorComplete(false);
        } else {
          setPeerAssessorComplete(true);
        }
      } else {
        setPeerAssessorComplete(false);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.requiredDimensionSlugs,
    props.numFriendsRequired,
    props.numSuccessfulInvitesRequired,
    props.numPeerRatingsRequired,
    props.numPeerAssessorsRequired,
    userState.memberProfile?.peerAssesseeUids?.length,
  ]);

  useEffect(() => {
    let error = true;
    if (props.requiredDimensionSlugs && !neededDimensionsCompleted) {
      error = false;
    }
    if (props.numSuccessfulInvitesRequired && !invitesComplete) {
      error = false;
    }
    if (props.numFriendsRequired && !connectionComplete) {
      error = false;
    }

    if (props.numPeerRatingsRequired && !peerRatingsComplete) {
      error = false;
    }

    if (props.numPeerAssessorsRequired && !peerAssessorComplete) {
      error = false;
    }
    setErrorState(error);

    ///error is true unless there is actually something to display for the discovery gate
  }, [
    remainingDimensionSlugs,
    neededDimensionsCompleted,
    connectionComplete,
    invitesComplete,
    peerRatingsComplete,
    peerAssessorComplete,
    props.requiredDimensionSlugs,
    props.numSuccessfulInvitesRequired,
    props.numFriendsRequired,
    props.numPeerRatingsRequired,
    props.numPeerAssessorsRequired,
  ]);

  return {
    remainingDimensionSlugs,
    errorState,
    neededDimensionsCompleted,
    connectionComplete,
    invitesComplete,
    peerRatingsComplete,
    peerRatingCount,
    peerAssessorComplete,
  };
}
